/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000130 部門照会
 */
import { Column, Query } from "@material-table/core";
import { B000120EigyoNm, catchRestError } from "clients/rest";
import { B000130Find, B000130FindRequest } from "clients/rest/b000/B000130";
import {
  SText,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";
import { B000120 } from "./B000120";

// 呼び出し時のプロパティ
export type B000130Props = Partial<{
  eigyoCd: string;
  // 削除予定区分チェック除外区分
  deljogaiKbn: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  eigyoCd: string;
  eigyoNm: string;
  bumonCdFrom: string;
  bumonCdTo: string;
  name: string;
  // 削除予定区分チェック除外区分(1の場合、削除予定区分を無視)
  deljogaiKbn: string;
}>;

// テーブル定義
type Data = Partial<{
  gyo: number;
  bumonCd: string;
  bumonNm: string;
  bumonNmr: string;
  bumonKana: string;
  telNo: string;
  faxNo: string;
  eigyoCd: string;
  sakujoyoteiKbn: number;
  eigyoNm: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  eigyoCd: "",
  eigyoNm: "",
  bumonCdFrom: "",
  bumonCdTo: "",
  name: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "2rem", align: "right" },
  { field: "bumonCd", title: "部門コード", width: "6rem" },
  { field: "bumonNm", title: "部門名", width: "10rem" },
  { field: "bumonKana", title: "部門カナ", width: "10rem" },
  { field: "eigyoNm", title: "営業所名", width: "10rem" },
  { field: "bumonNmr", hidden: true },
  { field: "telNo", hidden: true },
  { field: "faxNo", hidden: true },
  { field: "eigyoCd", hidden: true },
  { field: "sakujoyoteiKbn", hidden: true },
  { field: "version", hidden: true },
];

const focusId = "B000130";

// B000130 部門照会
export const B000130 = (props: B000130Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: B000130Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    (async () => {
      // 削除予定区分チェック除外区分処理
      if (props.deljogaiKbn) {
        methods.setValue("deljogaiKbn", props.deljogaiKbn);
      }
      if (!props.eigyoCd) {
        focus(1);
        // 初回フォーカス先
        return;
      }

      //営業所コード処理
      methods.setValue("eigyoCd", props.eigyoCd);
      await B000120EigyoNm(methods, navigate, setMsg);
      // 初回フォーカス先
      focus(2);
    })();
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButtun = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();
      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;
      // 検索パラメータを作成する。
      const param: B000130FindRequest = {
        eigyoCd: fields.eigyoCd,
        bumonCdFrom: fields.bumonCdFrom,
        bumonCdTo: fields.bumonCdTo,
        name: fields.name,
        // 削除予定区分チェック除外区分
        deljogaiKbn: fields.deljogaiKbn,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        // ["出力項目名,順番(asc/desc", ...]
        sort: ["bumonCd,asc"],
      };
      // API呼び出し
      B000130Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="部門照会"
        gridTemplateColumns="6rem 8rem 6rem 6rem 6rem 6rem 1rem 6rem"
      >
        <SLabel>営業所</SLabel>
        <SNumberField
          name="eigyoCd"
          code
          size={6}
          onBlur={() => B000120EigyoNm(methods, navigate, setMsg)}
          {...register(1)}
          dialog={<B000120 />}
          handleSubmit={(data: any) => {
            if (typeof data === "undefined") return;
            methods.setValue("eigyoCd", data.eigyoCd);
            methods.setValue("eigyoNm", data.eigyoNm);
          }}
        />
        <STextField name="eigyoNm" size={40} disabled gridColumn="span 2" />
        <SLabel>部門</SLabel>
        <SNumberField name="bumonCdFrom" code size={6} {...register(2)} />
        <SText>～</SText>
        <SNumberField name="bumonCdTo" code size={6} {...register(3)} />
        <SLabel gridColumn="1">部門名</SLabel>
        <STextField
          name="name"
          size={40}
          gridColumn="span 3"
          {...register(4)}
        />
        <SNormalButton
          label={labels.buttons.search}
          gridRow={2}
          gridColumn={8}
          align="right"
          {...register(5)}
          onClick={handleClickSearchButtun}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
